import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, storage } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp, orderBy, limit
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import Webcam from "react-webcam";
import Swal from 'sweetalert2';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, Divider, Dialog, DialogTitle, DialogContent,
    DialogActions, Grid, MenuItem, Autocomplete, Tooltip, Stack, Chip
} from '@mui/material';
import {
    Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon,
    Add as AddIcon, Close as CloseIcon, CameraAlt as CameraIcon,
    Autorenew as RetakeIcon, Visibility as ViewIcon, AccountBalanceWallet
} from '@mui/icons-material';

const StudentInventory = () => {
    const [inventory, setInventory] = useState([]);
    const [students, setStudents] = useState([]);
    const [products, setProducts] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [viewRecord, setViewRecord] = useState(null);

    // Wallet State
    const [walletBalance, setWalletBalance] = useState(0);

    // Form State
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
    const [items, setItems] = useState([{ productId: '', qty: '', rate: '', total: 0, payType: 'Payable' }]);
    const [paymentSource, setPaymentSource] = useState('Manual'); // Manual or Wallet
    const [payMode, setPayMode] = useState('Cash');

    // Webcam State
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [showCamera, setShowCamera] = useState(false);

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userSnap = await getDoc(doc(db, "users", user.uid));
                if (userSnap.exists()) {
                    const sId = userSnap.data().schoolId;
                    setSchoolId(sId);
                    fetchInitialData(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Fetch Wallet Balance when student changes
    useEffect(() => {
        if (selectedStudent?.studentId) {
            fetchWalletBalance(selectedStudent.studentId);
        } else {
            setWalletBalance(0);
        }
    }, [selectedStudent]);

    const fetchWalletBalance = async (studentId) => {
        const q = query(collection(db, "wallet_transactions"), where("studentId", "==", studentId));
        const snap = await getDocs(q);
        const bal = snap.docs.reduce((acc, curr) =>
            curr.data().type === 'Credit' ? acc + Number(curr.data().amount) : acc - Number(curr.data().amount), 0);
        setWalletBalance(bal);
    };

    const fetchInitialData = async (sId) => {
        const admSnap = await getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("status", "==", "Active")));
        const list = await Promise.all(admSnap.docs.map(async (adm) => {
            const admData = adm.data();
            const sSnap = await getDoc(doc(db, "students", admData.studentId));
            const sData = sSnap.data();
            return {
                id: adm.id,
                studentId: admData.studentId,
                label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''}`.toUpperCase(),
            };
        }));
        setStudents(list);

        const pSnap = await getDocs(query(collection(db, "products"), where("schoolId", "==", sId)));
        setProducts(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        fetchInventory(sId);
    };

    const fetchInventory = async (sId) => {
        const q = query(collection(db, "studentInventory"), where("schoolId", "==", sId), orderBy("updatedAt", "desc"));
        const snap = await getDocs(q);
        setInventory(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        if (field === 'qty' || field === 'rate' || field === 'payType') {
            const q = parseFloat(newItems[index].qty) || 0;
            const r = parseFloat(newItems[index].rate) || 0;
            newItems[index].total = newItems[index].payType === 'Payable' ? q * r : 0;
        }
        setItems(newItems);
    };

    const addItemRow = () => setItems([...items, { productId: '', qty: '', rate: '', total: 0, payType: 'Payable' }]);
    const grandTotal = items.reduce((sum, item) => sum + (item.total || 0), 0);

    const capture = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        setImgSrc(imageSrc);
        setShowCamera(false);
    }, [webcamRef]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedStudent) return Swal.fire("Error", "Select Student", "error");

        if (paymentSource === 'Wallet' && walletBalance < grandTotal) {
            return Swal.fire("Insufficient Balance", `Wallet has ₹${walletBalance}, but Total is ₹${grandTotal}`, "warning");
        }

        setSaving(true);
        try {
            let photoUrl = imgSrc || null;
            if (imgSrc && imgSrc.startsWith('data:image')) {
                const storageRef = ref(storage, `inventory/${schoolId}/${Date.now()}.jpg`);
                await uploadString(storageRef, imgSrc, 'data_url');
                photoUrl = await getDownloadURL(storageRef);
            }

            const payload = {
                studentAdmissionId: selectedStudent.id,
                studentId: selectedStudent.studentId,
                studentName: selectedStudent.label,
                issueDate,
                items,
                grandTotal,
                paymentSource,
                payMode: paymentSource === 'Wallet' ? 'Wallet' : payMode,
                photoUrl,
                schoolId,
                updatedAt: serverTimestamp()
            };

            // 1. Save Inventory Record
            if (editId) {
                await updateDoc(doc(db, "studentInventory", editId), payload);
            } else {
                await addDoc(collection(db, "studentInventory"), { ...payload, createdAt: serverTimestamp() });
            }

            // 2. If Wallet, Add Debit Transaction
            if (paymentSource === 'Wallet' && grandTotal > 0) {
                await addDoc(collection(db, "wallet_transactions"), {
                    studentId: selectedStudent.studentId,
                    amount: grandTotal,
                    type: 'Debit',
                    description: `Inventory Issue: ${items.length} items`,
                    timestamp: serverTimestamp(),
                    dateTime: new Date().toLocaleString()
                });
            }

            Swal.fire("Success", "Inventory record saved", "success");
            handleClose();
            fetchInventory(schoolId);
        } catch (err) { console.error(err); }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setSelectedStudent(null);
        setItems([{ productId: '', qty: '', rate: '', total: 0, payType: 'Payable' }]);
        setPaymentSource('Manual');
        setImgSrc(null);
    };

    const filtered = inventory.filter(i => (i.studentName?.toLowerCase() || "").includes(searchTerm.toLowerCase()));
    const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    const removeItemRow = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
    };

    return (
        <Grid sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} md={11}>
                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" justifyContent="space-between" mb={3}>
                        <Typography variant="h5" fontWeight="700">Student Inventory</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>New Issue</Button>
                    </Stack>

                    <TextField
                        size="small" placeholder="Search student..." fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 3, maxWidth: 400 }}
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }}
                    />

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell>Sr. No.</TableCell> {/* Added Sr No */}
                                    <TableCell>Date</TableCell>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Items</TableCell>
                                    <TableCell>Total</TableCell>
                                    <TableCell>Payment</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.map((row, index) => (
                                    <TableRow key={row.id}>
                                        {/* Sr No Logic: (Current Page - 1) * Items Per Page + index + 1 */}
                                        <TableCell>{(page - 1) * itemsPerPage + index + 1}</TableCell>

                                        {/* Date formatted as DD-MM-YYYY */}
                                        <TableCell>{formatDate(row.issueDate)}</TableCell>

                                        <TableCell>{row.studentName}</TableCell>
                                        <TableCell>{row.items?.length} items</TableCell>
                                        <TableCell>₹{row.grandTotal}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.payMode}
                                                size="small"
                                                color={row.payMode === 'Wallet' ? 'secondary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => setViewRecord(row)} color="info">
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={async () => {
                                                    if (window.confirm("Are you sure you want to delete this record?")) {
                                                        // Restore Wallet Balance if applicable
                                                        if (row.payMode === 'Wallet' && row.grandTotal > 0) {
                                                            await addDoc(collection(db, "wallet_transactions"), {
                                                                studentId: row.studentId,
                                                                amount: row.grandTotal,
                                                                type: 'Credit',
                                                                description: `Restored: Deleted Inventory Issue`,
                                                                timestamp: serverTimestamp(),
                                                                dateTime: new Date().toLocaleString()
                                                            });
                                                        }
                                                        // Delete the actual record
                                                        await deleteDoc(doc(db, "studentInventory", row.id));
                                                        fetchInventory(schoolId);
                                                        // Refresh balance if the modal is currently open for this student
                                                        if (selectedStudent?.studentId === row.studentId) {
                                                            fetchWalletBalance(row.studentId);
                                                        }
                                                    }
                                                }}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>

                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

            {/* Entry Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
                <DialogTitle sx={{ fontWeight: 'bold' }}>Inventory Issue Entry</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2} sx={{ mb: 3 }}>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={5}>
                                <Autocomplete
                                    options={students}
                                    getOptionLabel={(o) => o.label || ""}
                                    value={selectedStudent}
                                    onChange={(e, v) => setSelectedStudent(v)}
                                    renderInput={(params) => <TextField {...params} label="Select Student" size="small" required />}
                                />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={3}>
                                <TextField fullWidth label="Date" type="date" size="small" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}>
                                <Paper sx={{ p: 1, bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed green' }}>
                                    <AccountBalanceWallet sx={{ color: 'green', mr: 1 }} />
                                    <Typography variant="subtitle2">Wallet Balance: <b>₹{walletBalance}</b></Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Typography variant="overline" color="textSecondary" fontWeight="bold">Item Details</Typography>
                        <Divider sx={{ mb: 2 }} />

                        {items.map((item, index) => (
                            <Grid className="row" spacing={1} key={index} alignItems="center" sx={{ mb: 1.5 }}>
                                <Grid className="col-sm-3 mb-3" item xs={12} sm={3}>
                                    <TextField select fullWidth label="Product" size="small" value={item.productId} onChange={(e) => handleItemChange(index, 'productId', e.target.value)} required>
                                        {products.map(p => <MenuItem key={p.id} value={p.id}>{p.productName}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid className="col-sm-1 mb-3" item xs={6} sm={1.5}>
                                    <TextField fullWidth label="Qty" size="small" type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} required />
                                </Grid>
                                <Grid className="col-sm-2 mb-3" item xs={6} sm={1.5}>
                                    <TextField fullWidth label="Rate" size="small" type="number" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} required />
                                </Grid>
                                <Grid className="col-sm-2 mb-3" item xs={6} sm={2}>
                                    <TextField select fullWidth label="Type" size="small" value={item.payType} onChange={(e) => handleItemChange(index, 'payType', e.target.value)}>
                                        <MenuItem value="Payable">Payable</MenuItem>
                                        <MenuItem value="Unpayable">Unpayable (Free)</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid className="col-sm-2 mb-3" item xs={6} sm={2}>
                                    <TextField fullWidth label="Total" size="small" value={item.total} disabled />
                                </Grid>
                                <Grid className="col-sm-1 mb-3" item xs={1}>
                                    <IconButton color="error" onClick={() => removeItemRow(index)} disabled={items.length === 1}><CloseIcon /></IconButton>
                                </Grid>
                            </Grid>
                        ))}

                        <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addItemRow} sx={{ mb: 3 }}>Add Row</Button>

                        <Grid container spacing={2}>
                            <Grid className="col-sm-4 mb-3" item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
                                    <Typography variant="subtitle2" mb={1}>Payment Information</Typography>
                                    <Stack direction="row" spacing={2}>
                                        <TextField select label="Pay From" size="small" fullWidth value={paymentSource} onChange={(e) => setPaymentSource(e.target.value)}>
                                            <MenuItem value="Manual">Manual (Cash/UPI)</MenuItem>
                                            <MenuItem value="Wallet">Student Wallet</MenuItem>
                                        </TextField>
                                        {paymentSource === 'Manual' && (
                                            <TextField select label="Mode" size="small" fullWidth value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                                                <MenuItem value="Cash">Cash</MenuItem>
                                                <MenuItem value="UPI">Online / UPI</MenuItem>
                                            </TextField>
                                        )}
                                    </Stack>
                                </Paper>
                            </Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} md={6}>
                                <Box textAlign="right">
                                    <Typography variant="h4" color="primary" fontWeight="900">Total: ₹{grandTotal}</Typography>
                                    {paymentSource === 'Wallet' && (
                                        <Typography variant="caption" color={walletBalance < grandTotal ? 'error' : 'success.main'}>
                                            Rem. Wallet: ₹{walletBalance - grandTotal}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Photo Evidence Section */}
                        <Typography variant="overline" color="textSecondary" fontWeight="bold" sx={{ mt: 3, display: 'block' }}>
                            Photo Evidence (Optional)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                            {showCamera ? (
                                <Stack alignItems="center" spacing={2}>
                                    <Webcam
                                        audio={false}
                                        ref={webcamRef}
                                        screenshotFormat="image/jpeg"
                                        videoConstraints={{ width: 1280, height: 720, facingMode: "user" }}
                                        style={{ width: '100%', maxWidth: 400, borderRadius: 8 }}
                                    />
                                    <Stack direction="row" spacing={2}>
                                        <Button variant="contained" color="primary" startIcon={<CameraIcon />} onClick={capture}>
                                            Capture Photo
                                        </Button>
                                        <Button variant="outlined" color="error" onClick={() => setShowCamera(false)}>
                                            Cancel
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : imgSrc ? (
                                <Stack alignItems="center" spacing={2}>
                                    <Box position="relative">
                                        <img src={imgSrc} alt="captured" style={{ width: '100%', maxWidth: 300, borderRadius: 8 }} />
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.7)' }}
                                            onClick={() => setImgSrc(null)}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Button variant="outlined" startIcon={<RetakeIcon />} onClick={() => setShowCamera(true)}>
                                        Retake Photo
                                    </Button>
                                </Stack>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<CameraIcon />}
                                    onClick={() => setShowCamera(true)}
                                    sx={{ py: 4, px: 6, borderStyle: 'dashed' }}
                                >
                                    Take Student Photo
                                </Button>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saving} color="success">
                            {saving ? 'Processing...' : 'Complete Transaction'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* View Record Dialog */}
            <Dialog open={Boolean(viewRecord)} onClose={() => setViewRecord(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 'bold' }}>View Inventory Record</DialogTitle>
                <DialogContent dividers>
                    {viewRecord && (
                        <Box>
                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Student Name</Typography>
                                    <Typography variant="subtitle1" fontWeight="bold">{viewRecord.studentName}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Issue Date</Typography>
                                    <Typography variant="subtitle1" fontWeight="bold">{formatDate(viewRecord.issueDate)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Payment Mode</Typography>
                                    <Chip label={viewRecord.payMode} size="small" color={viewRecord.payMode === 'Wallet' ? 'secondary' : 'default'} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">Grand Total</Typography>
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary">₹{viewRecord.grandTotal}</Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="overline" color="textSecondary" fontWeight="bold">Issued Items</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Qty</TableCell>
                                            <TableCell>Rate</TableCell>
                                            <TableCell>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {viewRecord.items?.map((item, idx) => {
                                            // Match the product ID to get the product name
                                            const product = products.find(p => p.id === item.productId);
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell>{product ? product.productName : 'Unknown Product'}</TableCell>
                                                    <TableCell>{item.qty}</TableCell>
                                                    <TableCell>₹{item.rate}</TableCell>
                                                    <TableCell>₹{item.total}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {viewRecord.photoUrl ? (
                                <Box textAlign="center" mt={2}>
                                    <Typography variant="overline" color="textSecondary" fontWeight="bold" display="block" mb={1}>
                                        Photo Evidence
                                    </Typography>
                                    <img
                                        src={viewRecord.photoUrl}
                                        alt="Student Evidence"
                                        style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', border: '1px solid #ddd' }}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary" fontStyle="italic" textAlign="center">
                                    No photo evidence available for this record.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setViewRecord(null)} variant="outlined">Close</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default StudentInventory;